import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { ticketsApi } from '../services/api';
import { toast } from 'react-hot-toast';
import ws from '../services/wsService';
import { useAuthStore } from '../stores/authStore';
import TicketDetailPage from './TicketDetailPage';
import {
  ArrowLeftIcon,
  TableCellsIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  InformationCircleIcon,
  ChatBubbleLeftRightIcon,
  LockClosedIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const INFO_KEY = 'ggdesk:workspaceInfoOpen';

/**
 * Рабочее окно — раскладка «как в Telegram».
 *  Слева   — список ЧАТОВ (клубов): аватар, имя, время и превью последнего
 *            сообщения, бейджи по заявкам. Папки: Все / В работе / Новые / Пауза.
 *  Центр   — шапка чата, над лентой ВКЛАДКИ-ЗАЯВКИ (как темы в группе ТГ:
 *            зелёная активная / оранжевая пауза / красная передана), сама лента
 *            и композер — это TicketDetailPage в режиме variant="chat".
 *  Справа  — «инфо о чате»: клуб, текущая заявка, действия, все заявки клуба.
 *  ЛКМ по вкладке — открыть заявку; ПКМ — Взять / Завершить / Пауза / Передать.
 *  Alt+↑ / Alt+↓ — переключение чатов. Логика заявок не менялась.
 */

interface RailTicket {
  id: string;
  ticketNumber: string;
  subject: string;
  description?: string;
  status: string;
  priority?: string;
  createdAt: string;
  updatedAt?: string;
  lastMessageAt?: string | null;
  lastMessage?: { content?: string; sender?: string; senderName?: string; messageType?: string } | null;
  assignedTo?: { id?: string; firstName?: string; lastName?: string; shiftStatus?: string | null } | null;
  supportLine?: number;
  escalatedAt?: string | null;
  telegramChatId?: string;
  telegramChatType?: string;
  telegramUsername?: string;
  telegramFirstName?: string;
  telegramLastName?: string;
  telegramChatTitle?: string;
}

type RailFilter = 'all' | 'in_progress' | 'new' | 'paused';
type Cat = 'active' | 'paused' | 'transferred';

const normalizeStatus = (s: string) => (s || '').replace(/-/g, '_');
const LIVE = ['new', 'assigned', 'in_progress', 'paused'];

const catOf = (t: RailTicket): Cat | null => {
  const s = normalizeStatus(t.status);
  if (!LIVE.includes(s)) return null;
  if ((t.supportLine || 1) === 2) return 'transferred';
  if (s === 'paused') return 'paused';
  return 'active';
};
const CAT_ORDER: Record<Cat, number> = { active: 0, paused: 1, transferred: 2 };
const CAT_META: Record<Cat, { label: string; dot: string; text: string; ring: string }> = {
  active: { label: 'активная', dot: 'bg-emerald-500', text: 'text-emerald-600', ring: 'border-emerald-500' },
  paused: { label: 'на паузе', dot: 'bg-amber-500', text: 'text-amber-600', ring: 'border-amber-500' },
  transferred: { label: 'на 2-й линии', dot: 'bg-red-500', text: 'text-red-600', ring: 'border-red-500' },
};
const STATUS_RU: Record<string, string> = {
  new: 'Новая', assigned: 'Назначена', in_progress: 'В работе', paused: 'На паузе', resolved: 'Решена', closed: 'Закрыта',
};
const PRIORITY_RU: Record<string, string> = { low: 'Низкий', medium: 'Средний', high: 'Высокий', urgent: 'Срочный' };

const clubName = (t: RailTicket): string => {
  if (t.telegramChatType === 'private') {
    return t.telegramUsername ? `@${t.telegramUsername}` : `${t.telegramFirstName || ''} ${t.telegramLastName || ''}`.trim() || 'Клиент';
  }
  return t.telegramChatTitle || 'Группа';
};
const initials = (name: string) =>
  name.replace(/^@/, '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('') || '?';

// Стабильный цвет аватара по имени (как в Telegram — у каждого чата свой).
const AVATAR_COLORS = [
  'from-rose-400 to-rose-600', 'from-orange-400 to-orange-600', 'from-violet-400 to-violet-600',
  'from-emerald-400 to-emerald-600', 'from-sky-400 to-sky-600', 'from-pink-400 to-pink-600',
  'from-indigo-400 to-indigo-600', 'from-teal-400 to-teal-600',
];
const avatarColor = (key: string) => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

// Время в списке чатов — как в ТГ: сегодня HH:mm, на этой неделе — день, иначе дата.
const chatTime = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  const diff = (now.getTime() - d.getTime()) / 86400000;
  if (diff < 7) return d.toLocaleDateString('ru-RU', { weekday: 'short' });
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
};
const timeAgo = (iso?: string) => {
  if (!iso) return '';
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'сейчас';
  if (m < 60) return `${m} мин`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ч`;
  return `${Math.floor(h / 24)} д`;
};

const chatKey = (t: RailTicket): string => t.telegramChatId || `t:${t.id}`;

const shortText = (t: RailTicket, n = 60) => {
  const d = (t.description || '').replace(/\s+/g, ' ').trim();
  const s = d || t.subject || '—';
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
};

const MEDIA_LABEL: Record<string, string> = {
  photo: '📷 Фото', document: '📄 Файл', voice: '🎤 Голосовое', audio: '🎵 Аудио', video: '🎬 Видео', sticker: 'Стикер',
};

interface Club {
  chatId: string;
  name: string;
  tickets: RailTicket[];
  counts: Record<Cat, number>;
  latest: number;
  lastTicket: RailTicket | null; // заявка с самым свежим сообщением — для превью
}

const WorkspacePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [tickets, setTickets] = useState<RailTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<RailFilter>('all');
  const [search, setSearch] = useState('');
  const [pinnedChatId, setPinnedChatId] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; t: RailTicket } | null>(null);
  const [tab, setTab] = useState<'messages' | 'internal'>('messages');
  const [infoOpen, setInfoOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(INFO_KEY) !== '0'; } catch { return true; }
  });
  const toggleInfo = () => setInfoOpen((v) => { const n = !v; try { localStorage.setItem(INFO_KEY, n ? '1' : '0'); } catch { /* ignore */ } return n; });

  const fetchRef = useRef<(opts?: { silent?: boolean }) => void>(() => {});
  const fetchTickets = useCallback(async (opts: { silent?: boolean } = {}) => {
    const { silent = false } = opts;
    try {
      if (!silent) setLoading(true);
      // compact=1 — без журналов истории/заметок/передач: ~10x меньше трафика.
      const res = await ticketsApi.getAll({ limit: 200, sortBy: 'createdAt', sortOrder: 'desc', compact: 1 });
      setTickets(res.data?.data?.tickets || []);
    } catch {
      if (!silent) toast.error('Ошибка загрузки списка заявок');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);
  fetchRef.current = fetchTickets;
  useEffect(() => { fetchRef.current(); }, []);

  useEffect(() => {
    let pending: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (pending) clearTimeout(pending);
      pending = setTimeout(() => { pending = null; fetchRef.current({ silent: true }); }, 700);
    };
    ws.joinDashboard();
    ws.on('new_ticket', schedule);
    ws.on('ticket_updated', schedule);
    ws.on('new_message', schedule);
    return () => {
      if (pending) clearTimeout(pending);
      ws.off('new_ticket', schedule);
      ws.off('ticket_updated', schedule);
      ws.off('new_message', schedule);
    };
  }, []);

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(null); };
    window.addEventListener('click', close);
    window.addEventListener('keydown', esc);
    return () => { window.removeEventListener('click', close); window.removeEventListener('keydown', esc); };
  }, [menu]);

  const live = useMemo(() => tickets.filter((t) => catOf(t) !== null), [tickets]);

  const clubs = useMemo<Club[]>(() => {
    const map = new Map<string, Club>();
    for (const t of live) {
      const key = chatKey(t);
      let c = map.get(key);
      if (!c) {
        c = { chatId: key, name: clubName(t), tickets: [], counts: { active: 0, paused: 0, transferred: 0 }, latest: 0, lastTicket: null };
        map.set(key, c);
      }
      c.tickets.push(t);
      const k = catOf(t);
      if (k) c.counts[k] += 1;
      // Порядок — по последнему СООБЩЕНИЮ человека (клуба или оператора);
      // системные сообщения бота и смена статуса не поднимают чат.
      const ts = new Date(t.lastMessageAt || t.createdAt).getTime();
      if (ts >= c.latest) { c.latest = ts; c.lastTicket = t; }
    }
    for (const c of Array.from(map.values())) {
      c.tickets.sort((a, b) => {
        const d = CAT_ORDER[catOf(a) as Cat] - CAT_ORDER[catOf(b) as Cat];
        if (d) return d;
        return new Date(b.lastMessageAt || b.createdAt).getTime() - new Date(a.lastMessageAt || a.createdAt).getTime();
      });
    }
    return Array.from(map.values()).sort((a, b) => b.latest - a.latest);
  }, [live]);

  const matchesFilter = (t: RailTicket) => {
    const s = normalizeStatus(t.status);
    if (filter === 'all') return true;
    if (filter === 'in_progress') return s === 'in_progress' || s === 'assigned';
    if (filter === 'new') return s === 'new';
    return s === 'paused';
  };
  const railClubs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clubs.filter((c) => c.tickets.some(matchesFilter))
      .filter((c) => !q || c.name.toLowerCase().includes(q) || c.tickets.some((t) => t.ticketNumber.includes(q) || (t.description || '').toLowerCase().includes(q)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubs, filter, search]);

  const counts = useMemo(() => ({
    all: live.length,
    in_progress: live.filter((t) => ['in_progress', 'assigned'].includes(normalizeStatus(t.status))).length,
    new: live.filter((t) => normalizeStatus(t.status) === 'new').length,
    paused: live.filter((t) => normalizeStatus(t.status) === 'paused').length,
  }), [live]);

  const selectedTicket = useMemo(() => tickets.find((t) => String(t.id) === String(id)) || null, [tickets, id]);
  const chatId = (selectedTicket && chatKey(selectedTicket)) || pinnedChatId;
  const club = useMemo(() => clubs.find((c) => c.chatId === chatId) || null, [clubs, chatId]);

  const openClub = (c: Club) => {
    setPinnedChatId(c.chatId);
    setTab('messages');
    const first = c.tickets.find((t) => catOf(t) === 'active') || c.tickets[0];
    navigate(first ? `/work/${first.id}` : '/work');
  };
  const openTicket = (t: RailTicket) => { setPinnedChatId(chatKey(t)); navigate(`/work/${t.id}`); };

  // Alt+↑ / Alt+↓ — соседний чат (как в Telegram).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
      if (railClubs.length === 0) return;
      e.preventDefault();
      const idx = railClubs.findIndex((c) => c.chatId === chatId);
      const next = e.key === 'ArrowDown' ? Math.min(railClubs.length - 1, idx + 1) : Math.max(0, idx - 1);
      if (next !== idx) openClub(railClubs[next]);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [railClubs, chatId]);

  // ── Действия (та же логика, что кнопки в карточке заявки) ────────────────
  const isMine = (t: RailTicket) => !!t.assignedTo?.id && String(t.assignedTo.id) === String(user?.id);
  const holderOnShift = (t: RailTicket) => ['online', 'busy', 'break'].includes(String(t.assignedTo?.shiftStatus || 'offline'));
  const holderOffline = (t: RailTicket) => !!t.assignedTo?.id && !isMine(t) && !holderOnShift(t);
  const holderName = (t: RailTicket) => `${t.assignedTo?.firstName || ''} ${t.assignedTo?.lastName || ''}`.trim();

  const refresh = () => fetchRef.current({ silent: true });
  const doStatus = async (t: RailTicket, status: string, okMsg: string, note?: string) => {
    setMenu(null);
    try { await ticketsApi.updateStatus(t.id, status, note); toast.success(okMsg); refresh(); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Не удалось выполнить'); }
  };
  const doResolve = (t: RailTicket) => {
    setMenu(null);
    const note = window.prompt('Что было сделано? (короткий комментарий)');
    if (!note || !note.trim()) return;
    doStatus(t, 'resolved', 'Заявка завершена', note.trim());
  };
  const doEscalate = async (t: RailTicket) => {
    setMenu(null);
    const reason = window.prompt('Причина передачи на 2-ю линию:');
    if (!reason || !reason.trim()) return;
    try { await api.post(`/api/tickets/${t.id}/escalate`, { reason: reason.trim() }); toast.success('Передано на 2-ю линию'); refresh(); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Не удалось передать'); }
  };

  const newCount = (c: Club) => c.tickets.filter((t) => normalizeStatus(t.status) === 'new' && !t.assignedTo?.id).length;
  const previewLine = (c: Club) => {
    const t = c.lastTicket;
    if (!t) return '';
    const lm = t.lastMessage;
    if (lm) {
      const body = lm.messageType && lm.messageType !== 'text' ? (MEDIA_LABEL[lm.messageType] || lm.messageType) + (lm.content ? ` ${lm.content}` : '') : (lm.content || '');
      const who = lm.sender === 'operator'
        ? ((lm.senderName || '').split(' ')[0] === (user?.firstName || '') ? 'Вы' : (lm.senderName || 'Оператор').split(' ')[0])
        : '';
      return (who ? `${who}: ` : '') + body.replace(/\s+/g, ' ');
    }
    return shortText(t, 90);
  };

  const hasSelection = !!(id || pinnedChatId);
  const folder = (key: RailFilter, label: string, n: number) => (
    <button
      key={key}
      onClick={() => setFilter(key)}
      className={`relative shrink-0 px-3 py-2 text-xs font-medium transition-colors border-b-2 ${
        filter === key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}{n > 0 && <span className="ml-1 text-[10px] opacity-70">{n}</span>}
    </button>
  );

  const actionButtons = (t: RailTicket, compact = false) => {
    const s = normalizeStatus(t.status);
    const cls = `px-2.5 py-1.5 rounded-md text-xs font-medium border border-border hover:bg-accent transition-colors ${compact ? '' : 'w-full text-left'}`;
    return (
      <>
        <button className={cls} onClick={() => doResolve(t)}>✓ Завершить</button>
        {s === 'paused'
          ? <button className={cls} onClick={() => doStatus(t, 'in_progress', 'Заявка возобновлена')}>▶ Снять с паузы</button>
          : <button className={cls} onClick={() => doStatus(t, 'paused', 'Заявка на паузе')}>⏸ На паузу</button>}
        {(t.supportLine || 1) !== 2 && (
          <button className={cls} onClick={() => doEscalate(t)}>↗ Передать второй линии</button>
        )}
      </>
    );
  };

  return (
    <div className="flex h-[calc(100dvh-8rem)] sm:h-[calc(100dvh-7rem)] lg:h-[calc(100dvh-6.5rem)] overflow-hidden rounded-lg border border-border bg-card">
      {/* ── СПИСОК ЧАТОВ ── */}
      <aside className={`${hasSelection ? 'hidden sm:flex' : 'flex'} flex-col w-full sm:w-80 lg:w-[340px] shrink-0 border-r border-border`}>
        <div className="px-3 pt-3 pb-2 flex items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск"
              className="w-full h-9 pl-8 pr-8 rounded-full bg-muted text-sm outline-none focus:ring-2 focus:ring-primary/40 placeholder:text-muted-foreground"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-2 text-muted-foreground hover:text-foreground"><XMarkIcon className="h-5 w-5" /></button>
            )}
          </div>
          <button onClick={() => fetchTickets()} className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-accent" title="Обновить"><ArrowPathIcon className="h-4 w-4" /></button>
          <Link to="/tickets" className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-accent" title="Классический список"><TableCellsIcon className="h-4 w-4" /></Link>
        </div>
        <div className="flex border-b border-border px-1 overflow-x-auto">
          {folder('all', 'Все', counts.all)}
          {folder('in_progress', 'В работе', counts.in_progress)}
          {folder('new', 'Новые', counts.new)}
          {folder('paused', 'Пауза', counts.paused)}
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-xs text-muted-foreground">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2" />Загрузка…
            </div>
          ) : railClubs.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">{search ? 'Ничего не найдено' : filter === 'all' ? 'Нет активных заявок' : 'Ничего в этой папке'}</div>
          ) : railClubs.map((c) => {
            const selected = c.chatId === chatId;
            const t = c.lastTicket;
            const mineTouch = c.tickets.some(isMine);
            return (
              <button
                key={c.chatId}
                onClick={() => openClub(c)}
                className={`w-full text-left flex items-center gap-3 px-3 py-2 transition-colors ${selected ? 'bg-primary text-primary-foreground' : 'hover:bg-accent/50'}`}
              >
                <div className="relative shrink-0">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${avatarColor(c.chatId)} text-white flex items-center justify-center text-base font-semibold`}>
                    {initials(c.name)}
                  </div>
                  {mineTouch && <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-500 ring-2 ${selected ? 'ring-primary' : 'ring-card'}`} title="Есть ваши заявки" />}
                  {newCount(c) > 0 && (
                    <span className={`absolute -top-0.5 -left-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ${selected ? 'ring-primary' : 'ring-card'}`} title="Новые заявки без оператора">
                      {newCount(c)}
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold truncate">{c.name}</span>
                    <span className={`ml-auto text-[11px] shrink-0 ${selected ? 'opacity-80' : 'text-muted-foreground'}`}>{chatTime(t?.lastMessageAt || t?.createdAt)}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-xs truncate flex-1 ${selected ? 'opacity-90' : 'text-muted-foreground'}`}>{previewLine(c)}</span>
                    <span className="flex items-center gap-1 shrink-0">
                      {c.counts.active > 0 && <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold flex items-center justify-center ${selected ? 'bg-white/25 text-white' : 'bg-emerald-500 text-white'}`}>{c.counts.active}</span>}
                      {c.counts.paused > 0 && <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold flex items-center justify-center ${selected ? 'bg-white/25 text-white' : 'bg-amber-500 text-white'}`}>{c.counts.paused}</span>}
                      {c.counts.transferred > 0 && <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold flex items-center justify-center ${selected ? 'bg-white/25 text-white' : 'bg-red-500 text-white'}`}>{c.counts.transferred}</span>}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      {/* ── ЧАТ ── */}
      <section className={`${hasSelection ? 'flex' : 'hidden sm:flex'} flex-col flex-1 min-w-0`}>
        {club ? (
          <>
            {/* Шапка чата */}
            <div className="flex items-center gap-3 px-3 py-2 border-b border-border">
              <button onClick={() => { setPinnedChatId(null); navigate('/work'); }} className="sm:hidden p-1 -ml-1 text-muted-foreground hover:text-foreground"><ArrowLeftIcon className="h-5 w-5" /></button>
              <button onClick={toggleInfo} className="flex items-center gap-3 min-w-0 text-left" title="Инфо о чате">
                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${avatarColor(club.chatId)} text-white flex items-center justify-center text-sm font-semibold shrink-0`}>{initials(club.name)}</div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-foreground truncate">{club.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {club.tickets.length} {club.tickets.length === 1 ? 'заявка' : club.tickets.length < 5 ? 'заявки' : 'заявок'}
                    {club.counts.active > 0 && <span className="text-emerald-600"> · активных {club.counts.active}</span>}
                    {club.counts.paused > 0 && <span className="text-amber-600"> · на паузе {club.counts.paused}</span>}
                    {club.counts.transferred > 0 && <span className="text-red-600"> · 2-я линия {club.counts.transferred}</span>}
                  </div>
                </div>
              </button>
              <div className="ml-auto flex items-center gap-1">
                <button
                  onClick={() => setTab(tab === 'internal' ? 'messages' : 'internal')}
                  className={`p-2 rounded-full transition-colors ${tab === 'internal' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-accent'}`}
                  title={tab === 'internal' ? 'Вернуться к чату с клубом' : 'Внутренний чат по заявке (клуб не видит)'}
                >
                  <LockClosedIcon className="h-5 w-5" />
                </button>
                <button onClick={toggleInfo} className={`hidden xl:inline-flex p-2 rounded-full transition-colors ${infoOpen ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-accent'}`} title="Инфо о чате">
                  <InformationCircleIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Вкладки-заявки (темы) */}
            <div className="flex items-stretch gap-1 px-2 border-b border-border overflow-x-auto bg-muted/30">
              {club.tickets.map((t) => {
                const k = catOf(t) as Cat;
                const selected = String(t.id) === String(id);
                return (
                  <button
                    key={t.id}
                    onClick={() => openTicket(t)}
                    onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setMenu({ x: e.clientX, y: e.clientY, t }); }}
                    title={`#${t.ticketNumber} · ${CAT_META[k].label}${t.assignedTo ? ` · ${holderName(t)}` : ''}\n${shortText(t, 200)}\nПКМ — действия`}
                    className={`shrink-0 max-w-[220px] flex items-center gap-2 px-3 py-2 text-xs border-b-2 transition-colors ${
                      selected ? `${CAT_META[k].ring} bg-card text-foreground` : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/40'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full shrink-0 ${CAT_META[k].dot}`} />
                    <span className={`truncate ${selected ? 'font-semibold' : ''}`}>{shortText(t, 56)}</span>
                    {holderOffline(t) && <span className="shrink-0 text-red-500" title={`${holderName(t)} не на смене — можно взять`}>●</span>}
                  </button>
                );
              })}
            </div>

            {/* Тело: лента + инфо */}
            <div className="flex-1 min-h-0 flex">
              <div className="flex-1 min-w-0 min-h-0 flex flex-col bg-muted/20">
                {id ? (
                  tab === 'internal' && (
                    <div className="px-3 py-1.5 text-[11px] text-primary bg-primary/5 border-b border-border flex items-center gap-1.5">
                      <LockClosedIcon className="h-3.5 w-3.5" /> Внутренний чат по заявке #{selectedTicket?.ticketNumber || ''} — клуб этого не видит
                    </div>
                  )
                ) : null}
                {id ? (
                  <div className="flex-1 min-h-0">
                    <TicketDetailPage key={id} ticketId={id} embedded variant="chat" tab={tab} />
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Выберите заявку во вкладках выше</div>
                )}
              </div>

              {/* Инфо о чате */}
              {infoOpen && (
                <aside className="hidden xl:flex w-72 shrink-0 border-l border-border flex-col overflow-y-auto">
                  <div className="p-4 flex flex-col items-center text-center border-b border-border">
                    <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${avatarColor(club.chatId)} text-white flex items-center justify-center text-2xl font-semibold`}>{initials(club.name)}</div>
                    <div className="mt-2 text-base font-semibold text-foreground">{club.name}</div>
                    {club.chatId && !club.chatId.startsWith('t:') && (
                      <div className="mt-1 flex items-center gap-3 text-xs">
                        <Link to={`/clients/${encodeURIComponent(club.chatId)}`} className="text-primary hover:underline">Карточка клиента</Link>
                        <Link to={`/clients/${encodeURIComponent(club.chatId)}/chat${id ? `?ticket=${id}` : ''}`} className="text-primary hover:underline" title="Вся переписка клуба с разметкой заявок">Чат клуба</Link>
                      </div>
                    )}
                  </div>

                  {selectedTicket && (
                    <div className="p-4 border-b border-border space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full ${CAT_META[catOf(selectedTicket) as Cat]?.dot || 'bg-muted-foreground'}`} />
                        <span className="font-semibold text-foreground">Заявка #{selectedTicket.ticketNumber}</span>
                        <Link to={`/tickets/${selectedTicket.id}`} className="ml-auto text-[11px] text-primary hover:underline">полная карточка</Link>
                      </div>
                      <div className="text-xs text-foreground/80 whitespace-pre-wrap break-words max-h-32 overflow-y-auto">{selectedTicket.description || selectedTicket.subject}</div>
                      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                        <dt className="text-muted-foreground">Статус</dt><dd className="text-foreground">{STATUS_RU[normalizeStatus(selectedTicket.status)] || selectedTicket.status}</dd>
                        <dt className="text-muted-foreground">Приоритет</dt><dd className="text-foreground">{PRIORITY_RU[selectedTicket.priority || ''] || selectedTicket.priority || '—'}</dd>
                        <dt className="text-muted-foreground">Оператор</dt>
                        <dd className="text-foreground">
                          {selectedTicket.assignedTo ? holderName(selectedTicket) : '—'}
                          {holderOffline(selectedTicket) && <span className="text-red-600"> · не на смене</span>}
                        </dd>
                        <dt className="text-muted-foreground">Линия</dt><dd className="text-foreground">{selectedTicket.supportLine || 1}-я</dd>
                        <dt className="text-muted-foreground">Создана</dt><dd className="text-foreground">{new Date(selectedTicket.createdAt).toLocaleString('ru-RU')} ({timeAgo(selectedTicket.createdAt)})</dd>
                      </dl>
                      <div className="pt-1 flex flex-col gap-1.5">{actionButtons(selectedTicket)}</div>
                    </div>
                  )}

                  <div className="p-4 space-y-1.5">
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Заявки клуба</div>
                    {club.tickets.map((t) => {
                      const k = catOf(t) as Cat;
                      const selected = String(t.id) === String(id);
                      return (
                        <button
                          key={t.id}
                          onClick={() => openTicket(t)}
                          onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setMenu({ x: e.clientX, y: e.clientY, t }); }}
                          className={`w-full text-left rounded-lg px-2.5 py-2 text-xs transition-colors ${selected ? 'bg-accent' : 'hover:bg-accent/50'}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${CAT_META[k].dot}`} />
                            <span className="font-semibold text-foreground">#{t.ticketNumber}</span>
                            <span className={`ml-auto text-[10px] ${CAT_META[k].text}`}>{CAT_META[k].label}</span>
                          </div>
                          <div className="text-muted-foreground line-clamp-2 mt-0.5">{shortText(t, 90)}</div>
                          {t.assignedTo && (
                            <div className="text-[10px] mt-0.5 text-muted-foreground">
                              {t.assignedTo && <>{isMine(t) ? 'у вас' : holderName(t)}{holderOffline(t) && <span className="text-red-600"> · не на смене</span>}</>}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </aside>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8 bg-muted/20">
            <ChatBubbleLeftRightIcon className="h-12 w-12 text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">Выберите чат слева, чтобы начать</p>
            <p className="text-xs text-muted-foreground/70 mt-1">Alt+↑ / Alt+↓ — переключение чатов</p>
          </div>
        )}
      </section>

      {/* ПКМ-меню по заявке */}
      {menu && (
        <div
          className="fixed z-50 min-w-[220px] rounded-xl border border-border bg-popover shadow-xl py-1 text-sm"
          style={{ left: Math.min(menu.x, window.innerWidth - 240), top: Math.min(menu.y, window.innerHeight - 200) }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-1.5 text-[11px] text-muted-foreground border-b border-border truncate">#{menu.t.ticketNumber} · {shortText(menu.t, 40)}</div>
          <div className="p-1 flex flex-col gap-0.5 [&>button]:border-0 [&>button]:rounded-md">{actionButtons(menu.t)}</div>
        </div>
      )}
    </div>
  );
};

export default WorkspacePage;
